/**
 * Project each figure's Define status from the definitions.
 *
 * Status is never entered and never stored, so it cannot disagree with the work that
 * produced it. The same rule runs in Python for the API's own reads;
 * `packages/contracts/fixtures/figure-definition-status.json` holds the two together, and
 * changing either implementation means adding a case there first.
 *
 * A figure the definitions never mention answers as not started, which is why this takes the
 * identifiers it must answer for rather than deriving them from the definitions.
 */

// These shapes read the fixture, which is a subset of the wire model. The fixture is what
// pins the two languages together.
export interface ElementRow {
  element_id: string;
  title: string;
  confirmed: boolean;
}

export interface AnnotationInstance {
  element_id: string;
}

export interface FigureDefinition {
  file_id: string;
  numbered: boolean;
  description: string;
  walkthrough?: string;
  walkthrough_file_ids?: string[];
  walkthrough_accepted?: boolean;
  // Optional as it is on the wire: a figure row written before anything was pinned has none.
  instances?: AnnotationInstance[];
}

export interface FigureDefinitions {
  // Both optional as they are on the wire, where each has a server-side default.
  registry?: ElementRow[];
  figures?: FigureDefinition[];
}

export const FIGURE_DEFINE_STATUSES = [
  "complete",
  "incomplete",
  "not_started",
] as const;

export type FigureDefineStatus = (typeof FIGURE_DEFINE_STATUSES)[number];

// Emptiness is defined against ASCII whitespace alone rather than String.trim, because
// Python and JavaScript disagree in both directions: str.strip removes U+0085 where
// String.trim does not, and String.trim removes a byte order mark where str.strip does not.
// A title pasted from a word processor would otherwise read as titled on one side and
// untitled on the other, flipping this figure's status on identical bytes.
const ASCII_WHITESPACE = /^[ \t\n\r\v\f]*$/;

/**
 * Whether a field counts as empty.
 *
 * Exported because the screen must mark an empty walk-through with the same rule
 * that decides whether the figure is walked through. Two readings of emptiness would
 * let a figure show as required and project as complete at once.
 */
export function blank(text: string): boolean {
  return ASCII_WHITESPACE.test(text);
}

/**
 * A walk-through with more words folded in after a blank line.
 *
 * One rule for every fold — the field on screen and the stored set — so the
 * two can never join the same transcript differently.
 */
export function joinWalkthrough(existing: string, addition: string): string {
  return blank(existing) ? addition : `${existing}\n\n${addition}`;
}

/** Whether the walk-through counts as captured: typed, and its read-back accepted. */
function captured(figure: FigureDefinition): boolean {
  return (
    !blank(figure.walkthrough ?? "") && (figure.walkthrough_accepted ?? false)
  );
}

/**
 * Whether every element the figure pins is settled in the registry: present, titled and
 * confirmed. A numbered figure with nothing pinned is not settled either, since numbering
 * it was a claim that there are elements to mark.
 */
function elementsSettled(
  registry: ReadonlyMap<string, ElementRow>,
  figure: FigureDefinition,
): boolean {
  const instances = figure.instances ?? [];
  return (
    instances.length > 0 &&
    instances.every((instance) => {
      const row = registry.get(instance.element_id);
      return row !== undefined && row.confirmed && !blank(row.title);
    })
  );
}

function registryOf(definitions: FigureDefinitions): Map<string, ElementRow> {
  // An element with no registry row reads as neither titled nor confirmed, matching the
  // demo, whose lookup degrades to a falsy object.
  return new Map(
    (definitions.registry ?? []).map((row) => [row.element_id, row]),
  );
}

/**
 * One figure, read once: its status and the two facts the status was decided from.
 *
 * Both projections below read this, so the maps are built and the predicates run
 * once per figure rather than once per projection.
 */
interface Assessment {
  status: FigureDefineStatus;
  numbered: boolean;
  /** Whether any walk-through text exists; captured needs its read-back accepted too. */
  typed: boolean;
  captured: boolean;
  settled: boolean;
}

function assess(
  definitions: FigureDefinitions,
  figureFileIds: readonly string[],
): Record<string, Assessment> {
  const byFile = new Map(
    (definitions.figures ?? []).map((figure) => [figure.file_id, figure]),
  );
  const registry = registryOf(definitions);

  const assessed: Record<string, Assessment> = {};
  for (const fileId of figureFileIds) {
    const figure = byFile.get(fileId);
    if (figure === undefined) {
      assessed[fileId] = {
        status: "not_started",
        numbered: false,
        typed: false,
        captured: false,
        settled: false,
      };
      continue;
    }

    // The brief description is not read: it is generated from the walk-through and
    // confirmed in Review, so it says nothing about what the attorney has done here.
    // Text alone is not capture: the walk-through counts only once its read-back is
    // accepted, so typed-but-unconfirmed holds at incomplete rather than complete.
    const typed = !blank(figure.walkthrough ?? "");
    const isCaptured = captured(figure);
    const settled = elementsSettled(registry, figure);
    let status: FigureDefineStatus;
    if (!figure.numbered) {
      status = isCaptured ? "complete" : typed ? "incomplete" : "not_started";
    } else if ((figure.instances ?? []).length === 0) {
      status = typed ? "incomplete" : "not_started";
    } else {
      status = settled && isCaptured ? "complete" : "incomplete";
    }
    assessed[fileId] = {
      status,
      numbered: figure.numbered,
      typed,
      captured: isCaptured,
      settled,
    };
  }
  return assessed;
}

export function projectFigureStatus(
  definitions: FigureDefinitions,
  figureFileIds: readonly string[],
): Record<string, FigureDefineStatus> {
  return assessSet(definitions, figureFileIds).statuses;
}

/**
 * How far the set has got, as the readout beside the navigator states it.
 *
 * Counts, not a list: the navigator already names which figures are which, and the
 * readout answers how much is left and of what kind. A figure lacking both a walk-through
 * and settled elements is counted under both, so each line reads true on its own. A
 * blank walk-through and one typed but not yet confirmed are told apart, because the
 * screen tells drafting and confirming apart and an attorney who has written the text
 * must not be told to write it.
 */
export interface SetReadiness {
  ready: number;
  total: number;
  /** Figures not yet ready with no walk-through text at all. */
  needWalkthrough: number;
  /** Figures not yet ready whose walk-through is typed but not yet confirmed. */
  needAcceptance: number;
  /** Numbered figures not yet ready with an element untitled, unconfirmed or unplaced. */
  needElements: number;
}

/**
 * Both projections from one reading of the set.
 *
 * The sidebar draws the tags and the readout together, and reading the set twice for
 * two halves of one panel is avoidable work on every keystroke of a walk-through.
 */
export function assessSet(
  definitions: FigureDefinitions,
  figureFileIds: readonly string[],
): { statuses: Record<string, FigureDefineStatus>; readiness: SetReadiness } {
  const statuses: Record<string, FigureDefineStatus> = {};
  const readiness: SetReadiness = {
    ready: 0,
    total: figureFileIds.length,
    needWalkthrough: 0,
    needAcceptance: 0,
    needElements: 0,
  };
  for (const [id, a] of Object.entries(assess(definitions, figureFileIds))) {
    statuses[id] = a.status;
    if (a.status === "complete") {
      readiness.ready += 1;
      continue;
    }
    if (!a.captured) {
      if (a.typed) readiness.needAcceptance += 1;
      else readiness.needWalkthrough += 1;
    }
    if (a.numbered && !a.settled) readiness.needElements += 1;
  }
  return { statuses, readiness };
}

/**
 * The readiness of the set, over the identifiers given. Ready is exactly the status
 * projection's complete, read from the same assessment rather than restated, so the
 * readout can never count a figure the navigator does not tag as done.
 */
export function setReadiness(
  definitions: FigureDefinitions,
  figureFileIds: readonly string[],
): SetReadiness {
  return assessSet(definitions, figureFileIds).readiness;
}
